// Adapter de reference data (backend → ReferenceBundle da UI).
// serviceConfigs ← serviceConfigs/previsions; sessions ← serviceConfigs/{scid}/sessions;
// offerings ← buildOfferings(reuso do domínio); presets ← journey-templates.
import type { ReferenceBundle, ServiceConfig, Session, ServiceCatalog, Preset, JourneyTemplate, JourneyTemplateStep, JourneyTemplateStatus } from '../../domain/types'
import { buildOfferings } from '../../domain/queues'
import type { ServiceConfigPrevisionDto, SessionDto, JourneyTemplateDto, JourneyTemplateStepDto, LocationServiceDto, LocationServiceConfigRefDto, AvailableDayDto, AvailableDaysResponse, SlotDto } from '../dto/reference.dto'
import type { AvailableDayInfo, DaySlot } from '../../domain/types'

let _uidCounter = 0

export const adaptTemplateStatus = (s: 'Draft' | 'Active' | 'Archived' | number | undefined): JourneyTemplateStatus => {
  if (s === 'Active' || s === 2) {return 'Active'}
  if (s === 'Archived' || s === 3) {return 'Archived'}
  return 'Draft'
}

export const adaptSameLevelPolicy = (s: 'BlockUntilPreviousResolved' | 'AllowConcurrentOnLevel' | number | undefined): JourneyTemplate['sameLevelPolicy'] =>
  s === 'AllowConcurrentOnLevel' || s === 2 ? 'AllowConcurrentOnLevel' : 'BlockUntilPreviousResolved'

export const adaptTemplateStepDto = (s: JourneyTemplateStepDto, idx: number): JourneyTemplateStep => ({
  id: s.id,
  templateId: s.templateId,
  clientStepId: s.clientStepId,
  serviceId: s.serviceId ?? s.ServiceId ?? 0,
  serviceConfigId: s.serviceConfigId ?? s.ServiceConfigId,
  sessionId: s.sessionId ?? s.SessionId,
  sequence: s.sequence ?? s.Sequence ?? idx + 1,
  sortOrder: s.sortOrder ?? idx + 1,
  required: s.required ?? true,
  allowSkip: s.allowSkip ?? false,
  estimatedDurationMinutes: s.estimatedDurationMinutes ?? s.EstimatedDurationMinutes,
  _uid: ++_uidCounter,
})

const templateStepsDtoArr = (tpl: JourneyTemplateDto): JourneyTemplateStepDto[] => {
  const withAliases = tpl as JourneyTemplateDto & { services?: JourneyTemplateStepDto[]; Services?: JourneyTemplateStepDto[]; Steps?: JourneyTemplateStepDto[] }
  return withAliases.steps ?? withAliases.Steps ?? withAliases.services ?? withAliases.Services ?? []
}

export const adaptTemplateDto = (dto: JourneyTemplateDto): JourneyTemplate => ({
  id: dto.id,
  providerId: dto.providerId ?? 0,
  locationId: dto.locationId ?? 0,
  name: dto.name ?? dto.Name ?? dto.label ?? dto.Label ?? '',
  description: dto.description ?? dto.Description ?? '',
  status: adaptTemplateStatus(dto.status),
  version: dto.version ?? 1,
  rootTemplateId: dto.rootTemplateId,
  sameLevelPolicy: adaptSameLevelPolicy(dto.sameLevelPolicy),
  createdBy: dto.createdBy ?? '',
  updatedBy: dto.updatedBy ?? '',
  created: dto.created ?? '',
  updated: dto.updated ?? '',
  etag: dto.eTag ?? dto.etag ?? dto.ETag ?? '',
  steps: templateStepsDtoArr(dto).map(adaptTemplateStepDto),
})
import { previsionServiceConfigId } from './scid-resolver'

const asNumber = (v: unknown): number | null => {
  const n = Number(v)
  return Number.isFinite(n) ? n : null
}

// Newtonsoft serializa TimeSpan? como "[-][d.]hh:mm:ss[.fffffff]"; o prefixo de dias e a
// fração de segundo são opcionais. NullValueHandling.Ignore faz o campo SUMIR do JSON
// quando não configurado — por isso `value` chega undefined, nunca null explícito.
// A fração de segundo é descartada (não arredonda o minuto pra cima por causa dela).
const TIMESPAN_RE = /^(-)?(?:(\d{1,8})\.)?(\d{1,2}):(\d{2}):(\d{2})(?:\.\d+)?$/
// TimeSpan.MaxValue.Days do .NET — acima disso não é um TimeSpan válido, é lixo/adversarial.
const MAX_TIMESPAN_DAYS = 10_675_199

export const parseTimeSpanMinutes = (value?: string): number | undefined => {
  if (!value) {return undefined}
  const match = TIMESPAN_RE.exec(value.trim())
  if (!match) {return undefined}
  const [, sign, days, hours, minutes, seconds] = match
  const daysNum = Number(days) || 0
  if (daysNum > MAX_TIMESPAN_DAYS) {return undefined}
  const totalSeconds =
    daysNum * 86400 +
    Number(hours) * 3600 +
    Number(minutes) * 60 +
    Number(seconds)
  const totalMinutes = Math.round(totalSeconds / 60)
  return sign ? -totalMinutes : totalMinutes
}

const asString = (v: unknown): string => {
  if (v == null) {return ''}
  if (typeof v === 'string') {return v}
  if (typeof v === 'object' && v !== null) {
    const o = v as Record<string, unknown>
    const fallback = o['pt-BR'] || o['en-US'] || o.name || o.Name || o.label || o.Label || o.title || o.roomName || o.RoomName || o.serviceRoomName
    if (fallback && typeof fallback === 'string') {return fallback}
    return '' // Fallback vazio para não exibir [object Object]
  }
  const str = String(v)
  return str === '[object Object]' ? '' : str
}

const uniqueNums = (values: Array<number | null | undefined>): number[] =>
  [...new Set(values.filter((v): v is number => Number.isFinite(v)))]

const hhmm = (iso?: string): string | null => {
  if (!iso) {return null}
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) {return null}
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`
}

const serviceIdFromAny = (x: { id?: number; Id?: number; serviceId?: number; ServiceId?: number; service?: { id?: number; Id?: number } }): number | null =>
  asNumber(x.serviceId ?? x.ServiceId ?? x.service?.id ?? x.service?.Id ?? x.id ?? x.Id)

const serviceIdsFromPrevision = (p: ServiceConfigPrevisionDto): number[] =>
  uniqueNums([
    ...(p.serviceIds ?? []),
    ...(p.ServiceIds ?? []),
    ...(p.serviceConfig?.serviceIds ?? []),
    ...(p.serviceConfig?.ServiceIds ?? []),
    ...(p.ServiceConfig?.serviceIds ?? []),
    ...(p.ServiceConfig?.ServiceIds ?? []),
    ...(p.services ?? []).map(serviceIdFromAny),
    ...(p.Services ?? []).map(serviceIdFromAny),
    ...(p.serviceProvisions ?? []).map(serviceIdFromAny),
    ...(p.ServiceProvisions ?? []).map(serviceIdFromAny),
  ])

export const adaptServiceConfig = (p: ServiceConfigPrevisionDto): ServiceConfig => ({
  serviceConfigId: previsionServiceConfigId(p) ?? 0,
  nome: asString(p.serviceConfigName ?? p.ServiceConfigName ?? p.serviceConfig?.name ?? p.serviceConfig?.Name ?? p.ServiceConfig?.name ?? p.ServiceConfig?.Name ?? p.name ?? p.Name),
  sala: asString(p.roomName ?? p.RoomName ?? p.serviceRoomName ?? p.ServiceRoomName ?? p.serviceRoom?.name ?? p.ServiceRoom?.name ?? p.room?.name ?? p.Room?.name),
  code: asString(p.prefix ?? p.Prefix ?? p.serviceConfig?.prefix ?? p.serviceConfig?.Prefix ?? p.ServiceConfig?.prefix ?? p.ServiceConfig?.Prefix),
  serviceIds: serviceIdsFromPrevision(p),
  estMin: parseTimeSpanMinutes(p.averageTime ?? p.AverageTime),
})

const sessionServiceIds = (s: SessionDto): number[] | undefined => {
  const ids = uniqueNums([...(s.serviceIds ?? []), ...(s.ServiceIds ?? [])])
  return ids.length ? ids : undefined
}

const sessionLabel = (s: SessionDto): string => {
  const explicit = s.name ?? s.Name
  if (explicit) {
    const parsed = asString(explicit)
    if (parsed && parsed !== '[object Object]') {return parsed}
  }
  const start = hhmm(s.start ?? s.Start ?? s.startDate ?? s.StartDate)
  const end = hhmm(s.end ?? s.End ?? s.endDate ?? s.EndDate)
  if (start && end && start !== end) {return `${start}-${end}`}
  if (start) {return start}
  return 'Dia inteiro'
}

export const adaptSession = (s: SessionDto): Session => ({
  sessionId: s.id ?? s.Id ?? 0,
  serviceConfigId: s.serviceConfigId ?? s.ServiceConfigId ?? s.serviceConfig?.id ?? s.ServiceConfig?.id ?? s.ServiceConfig?.Id ?? 0,
  label: sessionLabel(s),
  start: s.start ?? s.Start ?? s.startDate ?? s.StartDate,
  end: s.end ?? s.End ?? s.endDate ?? s.EndDate,
  serviceIds: sessionServiceIds(s),
})

export const adaptAvailableDay = (serviceConfigId: number, d: AvailableDayDto): AvailableDayInfo => ({
  date: d.date,
  serviceConfigId,
  availableCount: d.availableCount,
  firstStart: d.firstStart,
  lastEnd: d.lastEnd,
})

export const adaptSlot = (s: SlotDto): DaySlot => ({
  sessionId: s.sessionId,
  label: s.label,
  start: s.start,
  end: s.end,
  available: s.available,
  capacity: s.capacity,
  remainingCapacity: s.remainingCapacity,
})

type TemplateListResponse = JourneyTemplateDto[] | {
  items?: JourneyTemplateDto[]
  data?: JourneyTemplateDto[]
  result?: JourneyTemplateDto[]
  results?: JourneyTemplateDto[]
}

export type ServiceListResponse = LocationServiceDto[] | {
  items?: LocationServiceDto[]
  data?: LocationServiceDto[]
  result?: LocationServiceDto[]
  results?: LocationServiceDto[]
}

const templateList = (tpls: TemplateListResponse): JourneyTemplateDto[] => {
  if (Array.isArray(tpls)) {return tpls}
  return tpls.items ?? tpls.data ?? tpls.result ?? tpls.results ?? []
}

const serviceList = (services?: ServiceListResponse): LocationServiceDto[] => {
  if (!services) {return []}
  if (Array.isArray(services)) {return services}
  return services.items ?? services.data ?? services.result ?? services.results ?? []
}

const templateSteps = (tpl: JourneyTemplateDto): JourneyTemplateStepDto[] => {
  const withAliases = tpl as JourneyTemplateDto & { services?: JourneyTemplateStepDto[]; Services?: JourneyTemplateStepDto[]; Steps?: JourneyTemplateStepDto[] }
  return withAliases.steps ?? withAliases.Steps ?? withAliases.services ?? withAliases.Services ?? []
}

export const adaptPresets = (tpls: TemplateListResponse): Record<string, Preset> => {
  const out: Record<string, Preset> = {}
  for (const tpl of templateList(tpls)) {
    const withAliases = tpl as JourneyTemplateDto & { Name?: string; label?: string; Label?: string; Description?: string }
    const name = tpl.name ?? withAliases.Name ?? withAliases.label ?? withAliases.Label
    if (!name || tpl.id == null) {continue}
    if (tpl.status !== 'Active' && tpl.status !== 2) {continue}
    out[String(tpl.id)] = {
      id: tpl.id,
      label: name,
      desc: tpl.description ?? withAliases.Description ?? '',
      itens: templateSteps(tpl)
        .map((s) => ({
          serviceId: s.serviceId ?? s.ServiceId,
          serviceConfigId: s.serviceConfigId ?? s.ServiceConfigId,
          sessionId: s.sessionId ?? s.SessionId,
          sequence: s.sequence ?? s.Sequence ?? 0,
        }))
        .filter((s): s is { serviceId: number; serviceConfigId: number | undefined; sessionId: number | undefined; sequence: number } => s.serviceId != null),
    }
  }
  return out
}

const serviceDtoId = (s: LocationServiceDto): number | null => {
  return asNumber(s.serviceId ?? s.ServiceId ?? s.id ?? s.Id)
}

const serviceDtoEstMin = (s: LocationServiceDto): number => {
  const v = s.estimatedMinutes ?? s.EstimatedMinutes ?? s.estimatedTime ?? s.EstimatedTime
    ?? s.averageServiceTime ?? s.AverageServiceTime ?? s.duration ?? s.Duration ?? 0
  return Number.isFinite(v) ? Number(v) : 0
}

export const adaptService = (s: LocationServiceDto): ServiceCatalog | null => {
  const serviceId = serviceDtoId(s)
  if (serviceId == null) {return null}
  return {
    serviceId,
    code: s.code ?? s.Code ?? s.prefix ?? s.Prefix ?? '',
    nome: s.name ?? s.Name ?? s.serviceName ?? s.ServiceName ?? '',
    sala: s.roomName ?? s.RoomName ?? '',
    estMin: serviceDtoEstMin(s),
  }
}

const serviceConfigRefId = (c: LocationServiceConfigRefDto): number | null =>
  asNumber(c.serviceConfigId ?? c.ServiceConfigId ?? c.id ?? c.Id)

const serviceConfigRefName = (c: LocationServiceConfigRefDto): string =>
  asString(c.serviceConfigName ?? c.ServiceConfigName ?? c.name ?? c.Name)

const serviceConfigRefRoom = (c: LocationServiceConfigRefDto): string =>
  asString(c.roomName ?? c.RoomName ?? c.serviceRoomName ?? c.ServiceRoomName ?? c.serviceRoom?.name ?? c.ServiceRoom?.name ?? c.room?.name ?? c.Room?.name)

const serviceConfigRefCode = (c: LocationServiceConfigRefDto): string =>
  asString(c.code ?? c.Code ?? c.prefix ?? c.Prefix)

const serviceConfigRefsFromService = (s: LocationServiceDto): LocationServiceConfigRefDto[] => {
  const refs: LocationServiceConfigRefDto[] = []
  if (s.serviceConfig) {refs.push(s.serviceConfig)}
  if (s.ServiceConfig) {refs.push(s.ServiceConfig)}
  refs.push(...(s.serviceConfigs ?? []), ...(s.ServiceConfigs ?? []))
  for (const p of [...(s.serviceProvisions ?? []), ...(s.ServiceProvisions ?? [])]) {
    if (p.serviceConfig) {refs.push(p.serviceConfig)}
    else if (p.ServiceConfig) {refs.push(p.ServiceConfig)}
    else if (p.serviceConfigId != null || p.ServiceConfigId != null) {refs.push({ serviceConfigId: p.serviceConfigId, ServiceConfigId: p.ServiceConfigId })}
  }
  if (s.serviceConfigId != null || s.ServiceConfigId != null) {refs.push({ serviceConfigId: s.serviceConfigId, ServiceConfigId: s.ServiceConfigId })}
  return refs
}

const mergeServiceConfigsFromServices = (base: ServiceConfig[], serviceDtos?: ServiceListResponse): ServiceConfig[] => {
  const byId = new Map(base.map((c) => [c.serviceConfigId, { ...c, serviceIds: [...c.serviceIds] }]))
  for (const svcDto of serviceList(serviceDtos)) {
    const serviceId = serviceDtoId(svcDto)
    if (serviceId == null) {continue}
    for (const ref of serviceConfigRefsFromService(svcDto)) {
      const serviceConfigId = serviceConfigRefId(ref)
      if (serviceConfigId == null) {continue}
      const current = byId.get(serviceConfigId)
      if (current) {
        if (!current.serviceIds.includes(serviceId)) {current.serviceIds.push(serviceId)}
        current.nome ||= serviceConfigRefName(ref)
        current.sala ||= serviceConfigRefRoom(ref)
        current.code ||= serviceConfigRefCode(ref)
      } else {
        byId.set(serviceConfigId, {
          serviceConfigId,
          nome: serviceConfigRefName(ref),
          sala: serviceConfigRefRoom(ref),
          code: serviceConfigRefCode(ref),
          serviceIds: [serviceId],
        })
      }
    }
  }
  return [...byId.values()]
}

const mergeServiceConfigsFromSessions = (base: ServiceConfig[], sessions: Session[]): ServiceConfig[] => {
  const byId = new Map(base.map((c) => [c.serviceConfigId, { ...c, serviceIds: [...c.serviceIds] }]))
  for (const sess of sessions) {
    if (!sess.serviceIds?.length) {continue}
    const current = byId.get(sess.serviceConfigId)
    if (!current) {
      byId.set(sess.serviceConfigId, {
        serviceConfigId: sess.serviceConfigId,
        nome: '',
        sala: '',
        code: '',
        serviceIds: [...sess.serviceIds],
      })
      continue
    }
    for (const serviceId of sess.serviceIds) {
      if (!current.serviceIds.includes(serviceId)) {current.serviceIds.push(serviceId)}
    }
  }
  return [...byId.values()]
}

export const deriveServices = (serviceConfigs: ServiceConfig[], serviceDtos?: ServiceListResponse): ServiceCatalog[] => {
  const catalog = new Map<number, ServiceCatalog>()
  for (const s of serviceList(serviceDtos)) {
    const adapted = adaptService(s)
    if (adapted) {catalog.set(adapted.serviceId, adapted)}
  }

  const seen = new Map<number, ServiceCatalog>()
  for (const sc of serviceConfigs) {for (const sid of sc.serviceIds) {
    if (seen.has(sid)) {continue}
    const svc = catalog.get(sid)
    seen.set(sid, {
      serviceId: sid,
      code: svc?.code || sc.code,
      nome: svc?.nome || sc.nome || `Serviço ${sid}`,
      sala: svc?.sala || sc.sala,
      estMin: svc?.estMin ?? 0,
    })
  }}
  return [...seen.values()].sort((a, b) => a.nome.localeCompare(b.nome, 'pt-BR'))
}

export function referenceBundle(previsions: ServiceConfigPrevisionDto[], sessionDtos: SessionDto[], templates: TemplateListResponse, serviceDtos?: ServiceListResponse, availableDayResponses?: AvailableDaysResponse[]): ReferenceBundle {
  const sessions = sessionDtos.map(adaptSession)
  const availableDays = availableDayResponses?.flatMap((r) => r.availableDays.map((d) => adaptAvailableDay(r.serviceConfigId, d))) ?? []
  const serviceConfigs = mergeServiceConfigsFromSessions(
    mergeServiceConfigsFromServices(previsions.map(adaptServiceConfig), serviceDtos),
    sessions,
  )
  const services = deriveServices(serviceConfigs, serviceDtos)
  const svcMap: Record<number, ServiceCatalog> = Object.fromEntries(services.map((s) => [s.serviceId, s]))
  const offerings = buildOfferings(serviceConfigs, sessions, svcMap)
  const presets = adaptPresets(templates)
  // customerFields/journeyFields: sem endpoint de config ainda — o RealDataSource os
  // preenche com o catálogo mock local (stopgap). Default vazio para satisfazer o tipo.
  return { services, serviceConfigs, sessions, offerings, presets, customerFields: [], journeyFields: [], availableDays }
}
